import { useFormikContext } from 'formik';
import { motion } from 'framer-motion';
import { LegacyRef, useEffect, useState } from 'react';
import { FaChevronDown } from 'react-icons/fa6';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../../constants/settings';
import useUserClickObserver from '../../../hooks/useUserClickObserver';
import { SelectData } from '../../../types/common';

interface IAcademiaFormSelect {
  options: Array<SelectData>;
  optionDescription?: Array<string | React.ReactNode>;
  name: string;
  label: string;
  defaultValue: SelectData;
  setValueOnChange?: (value: SelectData) => void;
  resetSelectField?: boolean;
  setResetSelectField?: () => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  shouldCapitalized?: boolean;
  showSearchInput?: boolean;
  badge?: React.ReactNode;
}

const AcademiaFormSelect = ({
  options,
  optionDescription,
  name,
  label,
  defaultValue,
  setValueOnChange,
  showSearchInput,
  resetSelectField,
  setResetSelectField,
  isRequired,
  isDisabled,
  shouldCapitalized,
  badge,
}: IAcademiaFormSelect) => {
  // integration of react hooks here
  const [allOptions, setAllOptions] = useState<Array<SelectData>>(options);
  const [selectedOption, setSelectedOption] =
    useState<SelectData>(defaultValue);
  const [searchText, setSearchText] = useState<string>('');
  const [showOptions, setShowOptions] = useState<boolean>(false);

  // integration of custom hooks here
  const selectRef = useUserClickObserver(setShowOptions);

  // integration of formik hooks here
  const { setFieldValue, touched, errors } = useFormikContext<any>();

  // handler function to handle setting select value declared here
  const setSelectValueHandler = (option: SelectData) => {
    setFieldValue(name, option);
    setSelectedOption(option);
    setShowOptions(false);
    setSearchText('');
    setValueOnChange && setValueOnChange(option);
  };

  // resetting the select field value if required here
  useEffect(() => {
    if (resetSelectField) {
      setSelectedOption(SELECT_FIELD_DEFAULT_VALUE);
      setFieldValue(name, SELECT_FIELD_DEFAULT_VALUE);
      setResetSelectField && setResetSelectField();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSelectField, name]);

  // searching among the select options implemented here
  useEffect(() => {
    if (searchText) {
      setAllOptions(
        options.filter((option) =>
          option.name.toLowerCase().includes(searchText.toLowerCase())
        )
      );
    } else {
      setAllOptions(options);
    }
  }, [options, searchText]);

  // rendering custom select field component here
  return (
    <div>
      <label
        htmlFor={`input-${name}`}
        title={isRequired ? 'This field is required' : ''}
        className='block mb-1 text-legal-dark-blue font-medium text-base'
      >
        {label} {isRequired && <span className='text-base-red'>*</span>}
      </label>

      <motion.div
        initial={{ x: 0 }}
        animate={
          touched[name] && errors[name]
            ? {
                x: [0, -10, 0, 10, 0],
                transition: {
                  duration: 0.1,
                  repeat: 2,
                },
              }
            : { x: 0 }
        }
        tabIndex={isDisabled || !options.length ? -1 : 0}
        ref={selectRef as LegacyRef<HTMLDivElement>}
        className='relative focus:ring-0 focus:outline-none'
        onFocus={() => setShowOptions(true)}
        onBlur={() => setShowOptions(false)}
      >
        <div
          title={selectedOption.name}
          // onClick={() => setShowOptions((state) => (state ? !state : state))}
          className={`relative peer/select border-2 rounded-md p-3 w-full text-legal-dark-blue font-semibold duration-300 text-base ${
            touched[name] && errors[name]
              ? 'border-base-red'
              : 'border-legal-dark-blue '
          } ${shouldCapitalized ? 'capitalize' : ''} ${
            isDisabled || !options.length
              ? 'opacity-60 cursor-not-allowed'
              : 'active:scale-95 cursor-pointer'
          }`}
        >
          {selectedOption.name}

          <div
            className={`absolute right-5 top-1/2 -translate-y-1/2 duration-300 bg-base-white ${
              showOptions && !isDisabled && options.length
                ? 'rotate-180'
                : 'rotate-0'
            }`}
          >
            <FaChevronDown className='h-6 w-6' />
          </div>

          {badge && badge}
        </div>

        <motion.div
          initial={{ height: 0 }}
          animate={showOptions ? { height: 'fit-content' } : { height: 0 }}
          className='absolute left-0 top-full bg-base-white shadow-academia-sm w-full z-20 mt-2 rounded-md text-legal-dark-blue overflow-hidden text-base'
        >
          {showSearchInput && options.length > 5 ? (
            <div className='m-3'>
              <input
                tabIndex={-1}
                type='text'
                className='border-2 focus:ring-4 focus:ring-legal-dark-blue rounded-md px-3 py-2 w-full text-legal-dark-blue font-semibold duration-300 text-base border-legal-dark-blue active:scale-[0.98]'
                placeholder='Search...'
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          ) : null}

          <motion.ul
            initial={{ height: 0 }}
            animate={
              showOptions
                ? allOptions.length > 5
                  ? { height: 220 }
                  : { height: 'fit-content' }
                : { height: 0 }
            }
            className='academia-scrollbar overflow-y-auto'
          >
            {allOptions?.map((option, index) => (
              <motion.li
                initial={{ opacity: 0 }}
                animate={showOptions ? { opacity: 1 } : { opacity: 0 }}
                key={index}
                onClick={() => {
                  setSelectValueHandler(option);
                  selectRef.current?.blur();
                }}
                className={`flex flex-col md:flex-row items-start md:items-center gap-0 md:gap-2 py-2 px-3 hover:bg-gray-200 duration-300 cursor-pointer font-semibold ${
                  shouldCapitalized ? 'capitalize' : ''
                } ${
                  index !== allOptions.length - 1 && 'border-b border-gray-300'
                }`}
              >
                <p>{option.name}</p>
                {optionDescription?.length ? (
                  <p className='text-sm text-gray-400 font-semibold italic'>
                    {optionDescription[index]}
                  </p>
                ) : null}
              </motion.li>
            ))}
          </motion.ul>
        </motion.div>
      </motion.div>

      <motion.label
        initial={{ y: -20, opacity: 0, display: 'none' }}
        animate={
          touched[name] && errors[name]
            ? { y: 0, opacity: 1, display: 'block' }
            : {
                y: -20,
                opacity: 0,
                transitionEnd: { display: 'none' },
              }
        }
        htmlFor={`input-${name}`}
        className='block mt-1 text-base-red text-base'
      >
        {errors[name] as string}
      </motion.label>
    </div>
  );
};

export default AcademiaFormSelect;
